/**
 * use-push — push API hooks (ADM-PUSH-S001~S005)
 */

import type {
  CreatePushTaskRequest,
  ListPushTasksQuery,
  PushTaskResponse,
  PushTemplateResponse,
  TestSendPushTemplateRequest,
  TestSendPushTemplateResponse,
  UpsertPushTemplateRequest,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';
import { downloadAuthedCsv } from '~/api/download-authed-csv';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export const pushApi = {
  listTemplates: () =>
    apiRequest<PushTemplateResponse[]>({ method: 'GET', path: '/api/v1/push/templates' }),
  upsertTemplate: (body: UpsertPushTemplateRequest) =>
    apiRequest<PushTemplateResponse>({ method: 'PUT', path: '/api/v1/push/templates', body }),
  // ADM-PUSH-S006：返回「已入队」，到达真机最长约 5 分钟（A4 §7.6）
  testSendTemplate: (slug: string, body: TestSendPushTemplateRequest) =>
    apiRequest<TestSendPushTemplateResponse>({
      method: 'POST',
      path: `/api/v1/push/templates/${encodeURIComponent(slug)}/test-send`,
      body,
    }),
  // GET /push/tasks 是 offset 分页（server okOffsetPaged，total/page/limit/hasMore 在 envelope 顶层，默认 limit=200）；
  // 用 apiRequestOffsetPaged 解出 data + 真实 total，供 usePushHistoryPage 显示真实总数 / 总页数 + 跳任意页。
  // admin 后台 endpoint 免 C 端 cursor 契约（dev-docs 40-分页与排序，admin 列表默认 offset）。
  // filters（ADM-PUSH-S005）服务端下推：status/app_id 多值 → 重复 query 参数；target_scope 单值。
  listTasks: (
    page?: number,
    limit?: number,
    filters?: ListPushTasksQuery,
  ): Promise<OffsetPagedResult<PushTaskResponse>> =>
    apiRequestOffsetPaged<PushTaskResponse>({
      method: 'GET',
      path: '/api/v1/push/tasks',
      query: { ...(page ? { page } : {}), ...(limit ? { limit } : {}), ...(filters ?? {}) },
    }),
  createTask: (body: CreatePushTaskRequest) =>
    apiRequest<PushTaskResponse>({ method: 'POST', path: '/api/v1/push/tasks', body }),
  // 单任务详情（含 payload + target_app_id）：复制任务时取来预填 ComposeDialog。
  getTask: (id: string) =>
    apiRequest<PushTaskResponse>({ method: 'GET', path: `/api/v1/push/tasks/${id}` }),
  // 取消推送任务（仅待发布态；非可取消态后端 PUSH_TASK_NOT_CANCELLABLE）
  cancelTask: (id: string) =>
    apiRequest<PushTaskResponse>({ method: 'POST', path: `/api/v1/push/tasks/${id}/cancel` }),
  // 导出单任务送达数据 CSV（裸流 → 认证下载）
  exportTaskDelivery: (id: string) =>
    downloadAuthedCsv(`${API_BASE || ''}/api/v1/push/tasks/${id}/export`, `push-task-${id}.csv`),
};
